<script lang="ts">
import { defineComponent, inject, onMounted, ref, watch } from 'vue';
import { Ref } from "@vue/reactivity";
import { CalendarSwitchEvent } from "@/domain/search/entitites/calendar/calendar-switch-event.interface";
import { CalendarConfiguration } from "@/domain/search/entitites/calendar/calendar-configuration.interface";

export default defineComponent({
    setup() {
        const calendarType = ref('start');
        const shouldEmitDateChange = ref(true);
        const configuration: CalendarConfiguration | undefined = inject('configuration');
        const date: Ref<Date | null> = ref(null);

        watch(date, (newDate, oldDate) => {
            if (newDate === oldDate) {
                return;
            }

            const timestamp: number | null = newDate ? new Date(newDate).getTime() : null;
            emitTimestamp(timestamp);
        });

        function emitTimestamp(timestamp) {
            window.dispatchEvent(new CustomEvent('calendar', {
                detail: {
                    timestamp,
                    type: calendarType.value,
                }
            }));

            shouldEmitDateChange.value = true;
        }

        function setupEventListeners() {
            window.addEventListener('calendarSwitch', (event) => {
                const typedEvent = <CalendarSwitchEvent>event;
                calendarType.value = typedEvent.detail.type;
                shouldEmitDateChange.value = false;
                date.value = typedEvent.detail.startValue ? new Date(typedEvent.detail.startValue) : null;
            });
        }

        onMounted(() => {
            setupEventListeners();
        });

        return {
            date,
            calendarType,
            shouldEmitDateChange,
            configuration,
            emitTimestamp
        }
    }
});
</script>

<template>
    <div class="calendar-wrapper">
        <VDatePicker v-if="calendarType === 'start'"
                     color="dot-color"
                     :locale="configuration!.locale"
                     is-expanded
                     is-inline
                     transparent
                     :min-date="new Date()"
                     borderless
                     v-model="date"
                     mode="date"
        />

        <VDatePicker v-if="calendarType === 'end'"
                     color="dot-color"
                     :locale="configuration!.locale"
                     is-expanded
                     is-inline
                     transparent
                     :min-date="configuration && configuration.minDateForEnd ? configuration.minDateForEnd : new Date()"
                     borderless
                     v-model="date"
                     mode="date"
        />
    </div>
</template>

<style scoped>
.calendar-wrapper :deep(.vc-dot-color) {
    --vc-accent-50: var(--datepicker-highlight-color);
    --vc-accent-100: var(--datepicker-highlight-color);
    --vc-accent-200: var(--datepicker-highlight-color);
    --vc-accent-300: var(--datepicker-highlight-color);
    --vc-accent-400: var(--datepicker-highlight-color);
    --vc-accent-500: var(--datepicker-highlight-color);
    --vc-accent-600: var(--datepicker-highlight-color);
    --vc-accent-700: var(--datepicker-highlight-color);
    --vc-accent-800: var(--datepicker-highlight-color);
    --vc-accent-900: var(--datepicker-highlight-color);
}
</style>
